// Colas de trabajo de la liquidación (rediseño ago-2026 de driver-payments).
//
// Cada repartidor cae en UNA cola según lo que el operador tiene que HACER con él:
//   'validar'      → subió su documento y falta aceptarlo o rechazarlo.
//   'pagar'        → elegible: sin bloqueos y con saldo pendiente.
//   'observacion'  → algo impide pagarle (sin documento, rechazado, ficha o banco
//                    incompletos) o la semana aún no cierra.
//   'pagado'       → sin saldo pendiente en el período.
//
// Lógica pura y sin React para poder testearla igual que driverFilters.
import { driverPendingAmount } from './driverFilters';

// Identificación normalizada para COMPARAR (solo dígitos + K mayúscula): el mismo criterio
// que usa el backend (`normalizeTaxId`) y el resumen exportado. No valida el dígito
// verificador: acá solo se cruzan dos identificaciones, no se da de alta un RUT.
const normId = (value) => String(value ?? '').replace(/[^0-9kK]/g, '').toUpperCase();

export const QUEUE_ALL = 'todos';
export const QUEUES = ['validar', 'pagar', 'observacion', 'pagado'];

/**
 * RUTs del emisor registrados en los documentos del período (normalizados, sin vacíos).
 * Es el lado "emisor" del cruce contra el titular de la cuenta: la transferencia solo es
 * válida si el que recibe es el que emitió la factura/boleta.
 */
export function documentIssuerRuts(d) {
  return (d?.billingDocuments ?? [])
    .map((doc) => normId(doc?.docMeta?.emisorRut))
    .filter(Boolean);
}

/**
 * Clasifica un repartidor del summary en su cola, con los motivos que bloquean el pago.
 *
 * Reglas de elegibilidad ('pagar'):
 *  - saldo pendiente > 0 y la semana cerrada;
 *  - documento aceptado (salvo freelancers, que no emiten documento);
 *  - datos bancarios completos según el backend (`bankData.complete`), salvo
 *    freelancers: la planilla banco no los incluye, se les paga por otro canal.
 *    `bankData === undefined` (backend sin el campo) NO bloquea: no se inventan
 *    observaciones que el operador no puede resolver.
 *  - con `issuerMustMatch`: el RUT del titular de la cuenta debe ser el del emisor del
 *    documento (ver más abajo).
 *
 * @param {object} d fila de drivers[] del summary
 * @param {{ weekOpen: boolean, t?: (s: string, o?: object) => string, issuerMustMatch?: boolean }} opts
 * @returns {{ queue: string, blockers: string[], pending: number }}
 */
export function classifyDriver(d, { weekOpen, t, issuerMustMatch = false } = {}) {
  const tr = typeof t === 'function' ? t : (s) => s;
  const pending = driverPendingAmount(d);
  const bank = d.bankData; // resumen del backend; undefined = backend viejo sin el campo

  const blockers = [];
  if (!weekOpen && !d.isFreelancer) {
    // El backend deriva 'missing' | 'uploaded' | 'accepted' | 'rejected'; un backend
    // viejo puede omitir el campo (null/undefined) — se trata igual que 'missing'.
    if (d.billingDocStatus == null || d.billingDocStatus === 'missing') {
      blockers.push(tr('Aún no sube su documento tributario'));
    } else if (d.billingDocStatus === 'uploaded') {
      blockers.push(tr('Documento pendiente de validar'));
    } else if (d.billingDocStatus === 'rejected') {
      blockers.push(tr('Documento rechazado: debe subir otro'));
    }
    if (!d.billingDocType) {
      blockers.push(tr('No ha elegido factura o boleta en su ficha'));
    }
    // Cruce emisor/titular: la cuenta que recibe debe ser del EMISOR del documento. Si el
    // titular es otro RUT, la plata sale hacia una cuenta que no emitió la factura (es el
    // cruce "¿Emisor = titular?" del resumen exportado). Se evalúa solo con el documento
    // ACEPTADO: si falta subirlo o está por validar, el motivo del documento ya manda y no
    // tiene sentido pedir el cruce. Se evalúa solo con `issuerMustMatch` (operador chileno:
    // fuera de CL ese mismo campo lleva CUIT/NIT/cédula y el cruce no aplica).
    if (issuerMustMatch && d.billingDocStatus === 'accepted' && bank?.holderRut) {
      const docs = d.billingDocuments ?? [];
      const issuerRuts = documentIssuerRuts(d);
      const holderRut = normId(bank.holderRut);
      // Se exige documento por documento (rango libre = varios documentos aceptados): no
      // alcanza con que UNO cuadre, ni con que alguno tenga el RUT registrado.
      if (issuerRuts.length < docs.length) {
        blockers.push(
          tr('Documento aceptado sin RUT del emisor registrado: no se puede verificar que la cuenta sea del emisor'),
        );
      } else if (issuerRuts.some((rut) => rut !== holderRut)) {
        blockers.push(
          tr('La cuenta bancaria no está a nombre del emisor del documento (emisor {{emisor}} · titular {{titular}})', {
            emisor: issuerRuts.join(' / '),
            titular: holderRut,
          }),
        );
      }
    }
  }
  if (!d.isFreelancer) {
    if (bank === null) {
      blockers.push(tr('Sin datos bancarios: no puede entrar a la planilla del banco'));
    } else if (bank && !bank.complete) {
      blockers.push(bank.missingReason || tr('Datos bancarios incompletos'));
    }
  }

  let queue;
  if (pending === 0 && (d.daysCount ?? 0) > 0) {
    queue = 'pagado';
  } else if (weekOpen) {
    // La semana en curso no es accionable: ni validar ni pagar. La vista colapsa
    // las colas a "Todos", así que el valor concreto no se muestra.
    queue = 'observacion';
  } else if (d.billingDocStatus === 'uploaded' && !d.isFreelancer) {
    // 'uploaded' también cubre semanas mixtas (una aceptada y otra recién subida):
    // mientras quede un documento sin validar, la tarea del operador es validar.
    queue = 'validar';
  } else if (blockers.length === 0) {
    queue = 'pagar';
  } else {
    queue = 'observacion';
  }

  return { queue, blockers, pending };
}

/**
 * Total que DEBERÍA decir el documento impreso según su tipo:
 *  - factura → neto + IVA (totalPayable): la factura lleva el IVA.
 *  - boleta  → honorarios BRUTOS (neto + extras): la boleta no lleva IVA y la
 *    retención SII solo afecta el pago líquido, no el total impreso.
 * Comparar una boleta contra totalPayable marcaría "difiere ~15%" en TODAS.
 */
export function docExpectedTotal(d, doc) {
  // El tipo congelado en el documento manda; sin documento, `d.billingDocType` ya es el
  // tipo EFECTIVO que resolvió billing para el período (no el del perfil actual).
  const docType = doc?.docType ?? d.billingDocType;
  if (docType === 'boleta') return (d.totalNet ?? 0) + (d.extrasTotal ?? 0);
  return d.totalPayable ?? 0;
}

/**
 * Conciliación del documento: monto DECLARADO (docMeta.montoTotal, transcrito del PDF)
 * menos el que corresponde según el tipo (docExpectedTotal). 0 = cuadra exacto.
 *
 * Solo compara cuando la comparación es honesta: rango = una semana completa y UN solo
 * documento en el período. Con rango libre o varias semanas, el total del período no es
 * el total del documento y el delta mentiría. `null` = no comparable / sin datos.
 */
export function docDelta(d, { rangeIsWeek } = {}) {
  if (!rangeIsWeek) return null;
  const docs = d.billingDocuments ?? [];
  if (docs.length !== 1) return null;
  const monto = docs[0]?.docMeta?.montoTotal;
  if (monto == null) return null;
  return monto - docExpectedTotal(d, docs[0]);
}

/**
 * Total que debería decir el documento de UNA semana, para cada tipo posible. Es la misma
 * regla con la que billing verifica la aceptación (`expectedAmountFor`): sobre la base neta
 * de cada día de la semana (`baseOwed`, independiente del tipo),
 *  - factura → Σ round(base_día × (1 + IVA del día));
 *  - boleta  → Σ round(base_día) (redondeo por día, igual que la app y el correo de montos).
 * Sirve para que el operador elija el tipo con el monto a la vista: `docExpectedTotal` no
 * alcanza porque su factura es `totalPayable`, que está calculado con el tipo efectivo de
 * la fila (con una boleta efectiva, totalPayable es el líquido, no el total de factura).
 *
 * `null` si la semana no tiene días o algún día no trae `baseOwed` (backend sin el campo):
 * no se inventa un esperado que el backend no respaldaría.
 *
 * @param {object} d   fila de drivers[] del summary
 * @param {{ weekStart?: string, weekEnd?: string }} doc documento de la semana
 * @returns {{ factura: number, boleta: number } | null}
 */
export function docExpectedTotals(d, doc) {
  const from = doc?.weekStart;
  const to = doc?.weekEnd;
  if (!from || !to) return null;
  const days = (d?.days ?? []).filter((day) => day?.date >= from && day?.date <= to);
  if (days.length === 0 || days.some((day) => !Number.isFinite(Number(day.baseOwed ?? NaN)))) {
    return null;
  }
  let factura = 0;
  let boleta = 0;
  days.forEach((day) => {
    const base = Number(day.baseOwed);
    // Mismo guard que billing: un taxRate no numérico cae al IVA base.
    const rate = Number.isFinite(Number(day.taxRate)) ? Number(day.taxRate) : 0.19;
    factura += Math.round(base * (1 + rate));
    boleta += Math.round(base);
  });
  return { factura, boleta };
}

/**
 * ¿Se puede calcular en el front lo que debería decir el documento de una semana? Solo si
 * el rango cargado cubre la semana completa del documento (si no, faltan días y el esperado
 * saldría corto) y los retiros se leyeron (con pickups 'unavailable' la base no los incluye).
 * Si no es comparable, no se muestra esperado local: manda el 409 del backend.
 *
 * @param {{ weekStart?: string, weekEnd?: string }} doc
 * @param {{ from?: string, to?: string, pickupsUnavailable?: boolean }} range
 * @returns {boolean}
 */
export function docComparableInRange(doc, { from, to, pickupsUnavailable = false } = {}) {
  if (pickupsUnavailable) return false;
  if (!doc?.weekStart || !doc?.weekEnd || !from || !to) return false;
  return from <= doc.weekStart && doc.weekEnd <= to;
}
